/* eslint-disable max-lines, react/no-array-index-key, react/jsx-props-no-spreading */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  TextField as MuiTextField,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { inventoryLotsByLotIdApi } from 'api/inventory';
import EXISTING_LOT_REASON_OPTIONS from 'common/constants/existingLotReasons';
import TALLY_REASON_OPTIONS from 'common/constants/tallyReasons';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveFulfillingPharmacies } from 'store/selectors/facilitySelectors';
import { addLinesToReceiving } from 'store/thunks/stockManagementThunk';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import {
  NO_PASTE_INPUT_PROPS,
  areLineQuantitiesValid,
  budFieldError,
  describeLotDrug,
  formatPreviewQty,
  formatUnverifiedQty,
  getBudHelperText,
  getBudMinDate,
  getExistingLotForNumber,
  getLotConfirmHelperText,
  getLotEntriesForNdc,
  getLotHelperText,
  getLotNdcMismatch,
  getNdcHelperText,
  getOrderedHelperText,
  getReceivedHelperText,
  getShippedHelperText,
  getTallyState,
  getUnverifiedAtPharmacy,
  isBudValid,
  isExistingLotReasonComplete,
  isLotConfirmed,
  isTallyComplete,
  isValidLotNumber,
  isValidNdc,
  lotConfirmFieldError,
  lotFieldError,
  needsExistingLotReason,
  orderedFieldError,
  pickLotForBud,
  receivedFieldError,
  resolveBudFromLot,
  shippedFieldError,
} from '../utils/stockLineValidation';
import AddCatalogDialog from './AddCatalogDialog';
import AddInventoryItemDialog from './AddInventoryItemDialog';
import NdcAutocomplete from './NdcAutocomplete';

const EMPTY_SPLIT = { fulfillingPharmacyId: '', quantityExpected: '' };

const EMPTY_LINE = {
  ndc: '',
  ndcSelected: false,
  drugName: '',
  lotNumber: '',
  // Re-key confirmation of lotNumber. Entry-only: it gates save and is never sent to the API.
  confirmLotNumber: '',
  // POM-6144 %s every lot in the system carrying this line's lot number, across all NDCs, and
  // which lot number the answer is for. Entry-only, never sent. Held ON the line rather than in a
  // map beside it so it travels with the row when a row above is removed; this dialog identifies
  // rows by index, and a side map would hand one row's answer to another.
  lotEntries: [],
  lotEntriesFor: '',
  bud: null,
  // The date the lookup overwrote, when it differed from what was typed. Entry-only, never sent:
  // BUD locks once an existing lot supplies it, and silently swapping a date behind a disabled
  // field is how a receipt gets submitted that nobody described.
  budReplacedFrom: null,
  quantityOrdered: '',
  quantityShipped: '',
  totalQuantityReceived: '',
  // POM-6086 - why this lot number is already in inventory. Required only when it is, and only
  // when a tally is not already carrying the answer.
  existingLotReason: '',
  splits: [],
};

const REQUIRED_FIELDS = [
  'ndc',
  'lotNumber',
  'quantityOrdered',
  'quantityShipped',
  'totalQuantityReceived',
];

const isSplitValid = (split) =>
  split.fulfillingPharmacyId.trim() !== '' && Number(split.quantityExpected) >= 1;

const getSplitTotal = (line) =>
  line.splits.reduce((sum, s) => sum + (Number(s.quantityExpected) || 0), 0);

/**
 * The lookup answer, but only while it still describes the lot number in the field.
 *
 * `lotEntries` is not cleared when the operator edits the lot number - the next answer replaces
 * it - so anything read straight off it can outlive the number it was fetched for: a foreign-lot
 * banner headed with the NEW number, or a BUD locked to the old lot's date. Nothing is lost by
 * ignoring it: re-confirming the number fires a fresh lookup, and Save is held until it lands.
 */
const currentLotEntries = (line) => {
  const trimmed = String(line.lotNumber ?? '').trim();
  return trimmed && line.lotEntriesFor === trimmed ? (line.lotEntries ?? []) : [];
};

/**
 * POM-6144 — the typed lot number already belongs to a DIFFERENT drug.
 *
 * A manufacturer lot number identifies one product, so the same number under a second NDC means
 * the operator read the lot off the vial in their hand and recorded it against the wrong line.
 * The row it creates looks entirely legitimate afterwards: it dispenses through FEFO, it accepts
 * claims, and nothing downstream questions it.
 *
 * This dialog is the easiest place to make that mistake — the NDC comes pre-filled from an
 * existing receiving record, so the operator is only typing lot numbers, off a stack of vials.
 * It was also the only one of the three entry points with no check at all: New Stock Receipt
 * warns and gates, the Fill Product dialog disables Add lot, and `upsertInventoryReceiving` has
 * no server-side check to catch any of them.
 *
 * Only once an NDC is chosen: `line.ndc` is what the lot is compared against.
 */
const getForeignNdcLots = (line) =>
  line.ndcSelected ? getLotNdcMismatch({ entries: currentLotEntries(line), ndc: line.ndc }) : [];

/**
 * The existing lot this receipt is adding to, or null when the number is new for this drug.
 *
 * Narrowed to the line's own NDC first. The lookup is by lot number alone and deliberately
 * cross-NDC - that is what surfaces a lot recorded against the wrong drug - so a foreign row must
 * never be the one that supplies this line's BUD.
 */
const getExistingLot = ({ line, receivedAtPharmacyId }) =>
  line.ndcSelected
    ? pickLotForBud({
        entries: getLotEntriesForNdc({ entries: currentLotEntries(line), ndc: line.ndc }),
        receivedAtPharmacyId,
      })
    : null;

/**
 * POM-6086 - the row this lot number is ALREADY held as, dated or not.
 *
 * Separate from getExistingLot above, which wants a DATED row because it is choosing the BUD to
 * adopt. This one is answering "has this number been received before", and an undated row - a
 * claim raised at a fill window - answers yes.
 */
const getRepeatedLot = (line) =>
  line.ndcSelected
    ? getExistingLotForNumber({ entries: currentLotEntries(line), ndc: line.ndc })
    : null;

/**
 * The lot number has been confirmed but its lookup has not come back yet.
 *
 * Save is blocked while this is true, for the same reason the Fill Product dialog disables Add
 * lot: saving mid-lookup would commit the row before the one check that could refuse it.
 */
const isLotCheckPending = (line) => {
  const trimmed = String(line.lotNumber ?? '').trim();
  if (!isValidLotNumber(trimmed) || !isLotConfirmed(line.lotNumber, line.confirmLotNumber)) {
    return false;
  }
  return line.lotEntriesFor !== trimmed;
};

/**
 * POM-6111 — the unverified claim this receipt can settle, and the tally against it.
 *
 * An unverified balance is stock that was dispensed against a lot inventory never received: the
 * fill posted a claim rather than a decrement, and the two buckets are never summed. A receipt is
 * the evidence that stock was real, so it is the moment the claim can be closed - and this dialog
 * is the one that tops up an existing record, which is exactly where such a lot turns up.
 *
 * Without this, receiving 100 against a lot carrying a 50-unit claim left the lot at 100 on hand
 * AND 50 unverified: `upsertLot` credits remaining_qty and never touches unverified_qty, and core
 * only closes a claim when the line carries `unverifiedTally`.
 *
 * Narrowed to the line's NDC and to the receiving facility. A claim belongs to one lot row at one
 * site; a balance held elsewhere is not something this receipt can answer for, and a foreign
 * drug's claim is not this line's to settle at all.
 */
const getLineTally = ({ line, receivedAtPharmacyId }) => {
  if (!line.ndcSelected) return null;
  const unverified = getUnverifiedAtPharmacy({
    entries: getLotEntriesForNdc({ entries: currentLotEntries(line), ndc: line.ndc }),
    fulfillingPharmacyId: receivedAtPharmacyId,
  });
  if (!unverified) return null;
  return {
    unverified,
    state: getTallyState({
      unverifiedQty: unverified.unverifiedQty,
      quantityReceived: line.totalQuantityReceived,
      tallyQty: line.tallyQty,
    }),
  };
};

/**
 * What is left of the receipt to distribute, once the claim has taken its share.
 *
 * Units that settle a claim never reach a shelf - clearUnverifiedOnReceipt subtracts them from
 * remaining_qty as it closes the balance - so distributing against them would ship stock that was
 * spent explaining stock already gone.
 */
const getDistributableQty = ({ line, receivedAtPharmacyId }) => {
  const received = Number(line.totalQuantityReceived) || 0;
  const tally = getLineTally({ line, receivedAtPharmacyId });
  return Math.max(0, received - (tally?.state.effective ?? 0));
};

const isLineTallyComplete = ({ line, receivedAtPharmacyId }) => {
  const tally = getLineTally({ line, receivedAtPharmacyId });
  if (!tally) return true;
  return isTallyComplete({
    unverifiedQty: tally.unverified.unverifiedQty,
    quantityReceived: line.totalQuantityReceived,
    tallyQty: line.tallyQty,
    tallyReason: line.tallyReason,
  });
};

const isLineExistingLotReasonComplete = ({ line, receivedAtPharmacyId }) =>
  isExistingLotReasonComplete({
    existingLot: getRepeatedLot(line),
    tallyState: getLineTally({ line, receivedAtPharmacyId })?.state,
    existingLotReason: line.existingLotReason,
  });

/**
 * The existing-lot reason to SEND, which is only one that is still owed.
 *
 * A reason answered before the operator retyped the lot number describes a lot this line no
 * longer names. Recomputed from the lookup rather than cleared on edit, exactly as the tally is:
 * the payload is built from what the lookup currently says, so a stale entry-only value has no
 * route to the API.
 */
const getSendableExistingLotReason = ({ line, receivedAtPharmacyId }) => {
  const owed = needsExistingLotReason({
    existingLot: getRepeatedLot(line),
    tallyState: getLineTally({ line, receivedAtPharmacyId })?.state,
  });

  return owed ? String(line.existingLotReason ?? '').trim() : '';
};

// Mirrors the New Stock Receipt (AddStockDialog) validation rules.
const isLineValid = (line, receivedAtPharmacyId) => {
  const baseValid = REQUIRED_FIELDS.every((f) => String(line[f]).trim() !== '');
  if (!baseValid) return false;
  if (!isValidNdc(line.ndc) || !line.ndcSelected) return false;
  if (!isValidLotNumber(line.lotNumber)) return false;
  if (!isLotConfirmed(line.lotNumber, line.confirmLotNumber)) return false;
  if (getForeignNdcLots(line).length > 0) return false;
  if (!isBudValid(line.bud)) return false;
  if (!areLineQuantitiesValid(line)) return false;
  if (!isLineTallyComplete({ line, receivedAtPharmacyId })) return false;
  if (!isLineExistingLotReasonComplete({ line, receivedAtPharmacyId })) return false;
  if (!line.splits || line.splits.length === 0) return true;
  return (
    line.splits.every(isSplitValid) &&
    getSplitTotal(line) <= getDistributableQty({ line, receivedAtPharmacyId })
  );
};

/**
 * Why Save is unavailable, or '' when it is available.
 *
 * Gated on the whole dialog being valid rather than on a click, which is what New Stock Receipt
 * does: an active button beside four empty required fields reads as "this is ready to submit".
 *
 * Returns the reason rather than a boolean because a disabled button with nothing to explain it
 * is its own dead end. The inline field messages cover the rest - they turn on as soon as the
 * operator leaves a field, so an incomplete line always says which part of it is incomplete.
 */
const saveBlockedReason = (lines, receivedAtPharmacyId) => {
  if (lines.some(isLotCheckPending)) return 'Checking the lot number…';
  // Bound, not passed by reference: Array.every hands the callback an INDEX as its second
  // argument, which would arrive here as the pharmacy id.
  if (!lines.every((line) => isLineValid(line, receivedAtPharmacyId))) {
    return 'Enter the required details on every item to save.';
  }
  return '';
};

const AddLineDialog = ({ receivingId, receivedAtPharmacyId, open, onClose }) => {
  const dispatch = useDispatch();
  const [lines, setLines] = useState([{ ...EMPTY_LINE, splits: [] }]);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [ndcDialogOpen, setNdcDialogOpen] = useState(false);

  // POM-5827: active-only allowed pharmacies (auth list can include inactive ones).
  const fulfillingPharmacies = useSelector(selectActiveFulfillingPharmacies);

  const splitPharmacies = fulfillingPharmacies.filter(
    (p) => p.fulfillingPharmacyId !== receivedAtPharmacyId
  );

  const saveBlocked = saveBlockedReason(lines, receivedAtPharmacyId);

  // Named only when the facility is one this user can see. Falling back to the raw id, as the
  // receipt dialog does, prints a UUID at an operator - better to leave the clause out.
  const pharmacyNameById = (fulfillingPharmacyId) =>
    fulfillingPharmacies.find((p) => p.fulfillingPharmacyId === fulfillingPharmacyId)
      ?.fulfillingPharmacyName ?? '';

  const handleAddRow = () => setLines((prev) => [...prev, { ...EMPTY_LINE, splits: [] }]);
  const handleRemoveRow = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));
  const handleChange = (idx, field, value) =>
    setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, [field]: value } : line)));

  /**
   * Records the cross-NDC answer against every row currently showing that lot number.
   *
   * Matched by VALUE, not by row index. Rows here are identified by position and can be removed
   * while a request is in flight, so an index captured at call time can point at a different row
   * by the time the answer lands. Matching on the lot number cannot mis-attribute: any row asking
   * about that number wants exactly this answer, and two rows sharing a lot share it correctly.
   * It also makes an out-of-order response harmless without a sequence counter — a stale reply
   * only ever writes the answer for the number it asked about, onto rows still showing it.
   */
  const applyLotEntries = (lotNumber, entries) =>
    setLines((prev) =>
      prev.map((line) => {
        if (String(line.lotNumber ?? '').trim() !== lotNumber) return line;
        const answered = { ...line, lotEntries: entries, lotEntriesFor: lotNumber };
        // The existing lot's BUD OVERWRITES whatever was typed, and the field then locks -
        // expiration_date is part of a lot's identity, so a receipt naming a different date does
        // not describe this lot at all and the API refuses it rather than guessing. Same
        // precedence New Stock Receipt uses; resolveBudFromLot is the shared rule.
        const resolved = resolveBudFromLot({
          entries: getLotEntriesForNdc({ entries, ndc: line.ndc }),
          receivedAtPharmacyId,
          typedBud: line.bud,
        });
        if (!resolved) return answered;
        return { ...answered, bud: resolved.bud, budReplacedFrom: resolved.replacedFrom };
      })
    );

  /**
   * Looked up only once the re-entered value MATCHES, which is the same trigger New Stock Receipt
   * uses. That is what makes a debounce unnecessary here: confirmation happens on the last
   * keystroke of the second field, so this fires once per completed lot number rather than once
   * per character.
   */
  const lookupLotWhenConfirmed = ({ lotNumber, confirmLotNumber }) => {
    const trimmed = String(lotNumber ?? '').trim();

    if (!isValidLotNumber(trimmed) || !isLotConfirmed(lotNumber, confirmLotNumber)) return;
    if (lines.some((l) => l.lotEntriesFor === trimmed)) return;

    inventoryLotsByLotIdApi({ lotId: trimmed })
      .then((entries) => applyLotEntries(trimmed, entries ?? []))
      // Treated as "nothing found" rather than as a block. The check exists to stop a mistake,
      // not to make saving depend on a lookup being reachable, and an unanswerable call must not
      // strand an operator mid-receipt. Core refuses a genuinely foreign lot on the fill path
      // regardless.
      .catch(() => applyLotEntries(trimmed, []));
  };

  const handleAddSplit = (lineIdx) =>
    setLines((prev) =>
      prev.map((line, i) =>
        i === lineIdx ? { ...line, splits: [...line.splits, { ...EMPTY_SPLIT }] } : line
      )
    );

  const handleRemoveSplit = (lineIdx, splitIdx) =>
    setLines((prev) =>
      prev.map((line, i) =>
        i === lineIdx ? { ...line, splits: line.splits.filter((_, si) => si !== splitIdx) } : line
      )
    );

  const handleSplitChange = (lineIdx, splitIdx, field, value) =>
    setLines((prev) =>
      prev.map((line, i) =>
        i === lineIdx
          ? {
              ...line,
              splits: line.splits.map((s, si) => (si === splitIdx ? { ...s, [field]: value } : s)),
            }
          : line
      )
    );

  const handleClose = () => {
    setLines([{ ...EMPTY_LINE, splits: [] }]);
    setShowErrors(false);
    onClose();
  };

  const handleSave = () => {
    if (!lines.every((line) => isLineValid(line, receivedAtPharmacyId))) {
      setShowErrors(true);
      return;
    }

    const payload = lines.map((l) => ({
      ndc: l.ndc,
      lotNumber: l.lotNumber,
      bud: format(l.bud, 'yyyy-MM-dd'),
      quantityOrdered: Number(l.quantityOrdered),
      quantityShipped: Number(l.quantityShipped),
      totalQuantityReceived: Number(l.totalQuantityReceived),
      ...(l.splits.length > 0 && {
        splits: l.splits.map((s) => ({
          fulfillingPharmacyId: s.fulfillingPharmacyId,
          quantityExpected: Number(s.quantityExpected),
        })),
      }),
      // POM-6086 - a GraphQL ENUM, so an empty string is a request-level validation error rather
      // than a null: it has to be omitted, not blanked. Absent whenever the lot number is new, or
      // when the tally below is carrying the reason instead.
      ...(() => {
        const reason = getSendableExistingLotReason({ line: l, receivedAtPharmacyId });
        return reason ? { existingLotReason: reason } : {};
      })(),
      ...(() => {
        const tally = getLineTally({ line: l, receivedAtPharmacyId });
        if (!tally) return {};
        return {
          unverifiedTally: {
            inventoryLotId: tally.unverified.inventoryLotId,
            quantity: tally.state.effective ?? 0,
            // `reason` is a GraphQL ENUM, so an empty string is a request-level validation error
            // rather than a null - it has to be omitted, not blanked. It is only ever absent on a
            // zero tally, which the API accepts without one.
            ...(String(l.tallyReason ?? '').trim() ? { reason: l.tallyReason.trim() } : {}),
            ...(String(l.tallyNotes ?? '').trim() ? { notes: l.tallyNotes.trim() } : {}),
          },
        };
      })(),
    }));

    setSubmitting(true);
    dispatch(
      addLinesToReceiving({
        receivingId,
        lines: payload,
        onSuccess: handleClose,
      })
    ).finally(() => setSubmitting(false));
  };

  return (
    <>
      <Dialog
        open={open}
        // Closing discards every typed line (handleClose resets them), and unlike the new-stock
        // dialog there's no draft to fall back on. So an accidental backdrop click or Escape must
        // not dismiss it — closing is only via the explicit Cancel button.
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        disableEscapeKeyDown
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          Add Items
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              size='small'
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => setCatalogDialogOpen(true)}
            >
              Add to Catalog
            </Button>
            <Button
              size='small'
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => setNdcDialogOpen(true)}
            >
              Add NDC
            </Button>
          </Box>
        </DialogTitle>
        <Typography variant='body2' color='text.secondary' sx={{ px: 3, pb: 1 }}>
          Add one or more items to this existing stock receiving record. Each item requires an NDC,
          lot number, BUD, and quantity details.
        </Typography>
        <Divider />

        {/*
          Field messages were revealed by clicking Save. Save is now gated on validity, so that
          click no longer happens - focusout is the next-earliest moment the operator has finished
          with a field and can be told what is wrong with it. React's onBlur is focusout, which
          bubbles, so this covers every field in the dialog including the split rows.
        */}
        <DialogContent onBlur={() => setShowErrors(true)}>
          {lines.map((line, idx) => {
            const splitTotal = getSplitTotal(line);
            const receivedQty = Number(line.totalQuantityReceived) || 0;
            const lineTally = getLineTally({ line, receivedAtPharmacyId });
            // What a split can draw on, which is the receipt MINUS whatever settles the claim.
            const distributableQty = getDistributableQty({ line, receivedAtPharmacyId });
            const remaining = Math.max(0, distributableQty - splitTotal);
            const splitOverflow = line.splits.length > 0 && splitTotal > distributableQty;
            const tallyReasonMissing =
              showErrors &&
              (lineTally?.state.effective ?? 0) > 0 &&
              String(line.tallyReason ?? '').trim() === '';
            const usedPharmacyIds = line.splits.map((s) => s.fulfillingPharmacyId);
            const foreignNdcLots = getForeignNdcLots(line);
            const existingLot = getExistingLot({ line, receivedAtPharmacyId });
            // Both halves matter: the lookup found a dated lot AND that date is now in the field.
            // Locking an empty field would leave the line unsubmittable with no way to fill it.
            const budLocked = Boolean(existingLot) && Boolean(line.bud);
            // POM-6086 - any row with this number, which is what makes it a repeat. The notice
            // describes the dated row when there is one, because that is the row supplying the
            // BUD; an undated row is all there is to point at otherwise.
            const repeatedLot = getRepeatedLot(line);
            const noticeLot = existingLot ?? repeatedLot;
            const existingLotFacility = noticeLot
              ? pharmacyNameById(noticeLot.fulfillingPharmacyId)
              : '';
            const existingLotReasonNeeded = needsExistingLotReason({
              existingLot: repeatedLot,
              tallyState: lineTally?.state,
            });
            const existingLotReasonMissing =
              showErrors &&
              existingLotReasonNeeded &&
              String(line.existingLotReason ?? '').trim() === '';

            return (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={idx} sx={{ mb: 2, p: 1.5, border: '1px solid #e0e7f3', borderRadius: 1 }}>
                <Grid container spacing={1} alignItems='flex-start'>
                  <Grid item xs={2.5}>
                    <NdcAutocomplete
                      label='NDC *'
                      value={line.ndc}
                      onChange={({ ndc, drugName, isSelected }) =>
                        setLines((prev) =>
                          prev.map((l, i) =>
                            i === idx ? { ...l, ndc, drugName, ndcSelected: isSelected } : l
                          )
                        )
                      }
                      error={showErrors && (!isValidNdc(line.ndc) || !line.ndcSelected)}
                      helperText={
                        getNdcHelperText(showErrors, line.ndc, line.ndcSelected) || line.drugName
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Stack spacing={1}>
                      <MuiTextField
                        label='Lot # *'
                        size='small'
                        fullWidth
                        value={line.lotNumber}
                        onChange={(e) => {
                          handleChange(idx, 'lotNumber', e.target.value);
                          lookupLotWhenConfirmed({
                            lotNumber: e.target.value,
                            confirmLotNumber: line.confirmLotNumber,
                          });
                        }}
                        error={lotFieldError(showErrors, line.lotNumber)}
                        helperText={getLotHelperText(showErrors, line.lotNumber)}
                      />
                      <MuiTextField
                        label='Re-enter Lot # *'
                        size='small'
                        fullWidth
                        value={line.confirmLotNumber ?? ''}
                        onChange={(e) => {
                          handleChange(idx, 'confirmLotNumber', e.target.value);
                          lookupLotWhenConfirmed({
                            lotNumber: line.lotNumber,
                            confirmLotNumber: e.target.value,
                          });
                        }}
                        inputProps={NO_PASTE_INPUT_PROPS}
                        error={lotConfirmFieldError(
                          showErrors,
                          line.lotNumber,
                          line.confirmLotNumber
                        )}
                        helperText={getLotConfirmHelperText(
                          showErrors,
                          line.lotNumber,
                          line.confirmLotNumber
                        )}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={2}>
                    <DesktopDatePicker
                      // Remounted when the lock flips, which is the only way the field's own text
                      // catches up. The masked input keeps the string the operator typed in state
                      // of its own and rifm re-syncs it a render late, so adopting the existing
                      // lot's date left the OLD date on display in a field that had just locked -
                      // the exact confusion this is meant to remove. Nothing is lost: the
                      // transition only happens when a lookup lands, and the operator is in the
                      // lot fields then, not this one.
                      key={budLocked ? 'bud-locked' : 'bud-open'}
                      label='BUD *'
                      inputFormat='MM/dd/yyyy'
                      minDate={getBudMinDate()}
                      value={line.bud}
                      onChange={(newValue) => handleChange(idx, 'bud', newValue)}
                      disabled={budLocked}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          size='small'
                          fullWidth
                          // A locked date is not the operator's to answer for, so the usual
                          // "you have not filled this in" state does not apply. It still flags
                          // when the adopted date is itself unrecordable, because that leaves the
                          // line unsubmittable and the field cannot be edited to fix it.
                          error={
                            budLocked ? !isBudValid(line.bud) : budFieldError(showErrors, line.bud)
                          }
                          helperText={(() => {
                            if (budLocked && !isBudValid(line.bud)) {
                              return getBudHelperText(true, line.bud);
                            }
                            // Say so when a typed date was replaced. Silently overwriting a date
                            // the user entered - into a field that then locks - is how they
                            // submit a receipt they did not describe.
                            if (budLocked && line.budReplacedFrom) {
                              return `Replaced ${format(line.budReplacedFrom, 'MM/dd/yyyy')}`;
                            }
                            if (budLocked) return 'From the existing lot';
                            return getBudHelperText(showErrors, line.bud);
                          })()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={1.5}>
                    <MuiTextField
                      label='Ordered *'
                      size='small'
                      fullWidth
                      type='number'
                      inputProps={{ min: 1 }}
                      value={line.quantityOrdered}
                      onChange={(e) => handleChange(idx, 'quantityOrdered', e.target.value)}
                      error={orderedFieldError(showErrors, line)}
                      helperText={getOrderedHelperText(showErrors, line)}
                    />
                  </Grid>
                  <Grid item xs={1.5}>
                    <MuiTextField
                      label='Shipped *'
                      size='small'
                      fullWidth
                      type='number'
                      inputProps={{ min: 1 }}
                      value={line.quantityShipped}
                      onChange={(e) => handleChange(idx, 'quantityShipped', e.target.value)}
                      error={shippedFieldError(showErrors, line)}
                      helperText={getShippedHelperText(showErrors, line)}
                    />
                  </Grid>
                  <Grid item xs={1.5}>
                    <MuiTextField
                      label='Received *'
                      size='small'
                      fullWidth
                      type='number'
                      inputProps={{ min: 1 }}
                      value={line.totalQuantityReceived}
                      onChange={(e) => handleChange(idx, 'totalQuantityReceived', e.target.value)}
                      error={receivedFieldError(showErrors, line)}
                      helperText={getReceivedHelperText(showErrors, line)}
                    />
                  </Grid>
                  <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size='small'
                      color='error'
                      onClick={() => handleRemoveRow(idx)}
                      disabled={lines.length === 1}
                      aria-label='Remove item'
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </Grid>
                </Grid>

                {/*
                  Shown regardless of showErrors, unlike the field-level messages around it.
                  Those say "you have not finished"; this states a fact about the lot that is
                  true the moment it is known, and waiting for a Save click to reveal it would
                  let the operator keep typing quantities against the wrong drug.
                */}
                {foreignNdcLots.length > 0 && (
                  <Alert severity='error' sx={{ mt: 1.5 }} data-testid='foreign-ndc-lot-notice'>
                    <strong>{String(line.lotNumber).trim()}</strong> already belongs to{' '}
                    {foreignNdcLots.map(describeLotDrug).join(', ')}. A lot number belongs to one
                    product, so either the NDC or the lot number is wrong — check the vial.
                  </Alert>
                )}

                {/*
                  Why the BUD above is filled in and locked, and what else is already on this lot.
                  Info rather than warning: adding to an existing lot is the ordinary case here -
                  this record is being topped up - and the operator only needs to know that the
                  date is not theirs to answer for, and that the balances they are adding to are
                  not what a plain receipt would imply.
                */}
                {noticeLot && (
                  <Alert severity='info' sx={{ mt: 1.5 }} data-testid='existing-lot-notice'>
                    <strong>{String(line.lotNumber).trim()}</strong> already exists for{' '}
                    {describeLotDrug(noticeLot)}
                    {existingLotFacility ? ` at ${existingLotFacility}` : ''} — on hand{' '}
                    {formatUnverifiedQty(noticeLot.remainingQty)}
                    {/* Only when the tally section below is not the one saying it. There it is a
                        number the operator has to answer for; here it would be a second copy of
                        it, and a claim held at ANOTHER facility is the only case this dialog can
                        state but not settle. */}
                    {!lineTally && Number(noticeLot.unverifiedQty) > 0
                      ? `, ${formatUnverifiedQty(noticeLot.unverifiedQty)} ${SYSTEM_DEFICIT.toLowerCase()}`
                      : ''}
                    .{' '}
                    {existingLot
                      ? `BUD comes from that lot and cannot be changed here: the expiration date is
                         part of a lot's identity, so a receipt naming a different date describes a
                         different lot.`
                      : 'That record has no expiration date yet, so the one entered above is the ' +
                        'date this receipt establishes for it.'}
                  </Alert>
                )}

                {/*
                  POM-6086 — the repeat is allowed; what it needs is an explanation. On-hand for
                  this lot is about to rise a second time and nothing else on the row says why, so
                  the audit report has nothing to group on. No override and no merge: the answer
                  is one of three, and the system cannot infer which because nothing it can see
                  distinguishes them.

                  Hidden when a tally is already collecting a reason for this same lot in this
                  same act — the API takes that one as the answer to both questions. A tally of
                  ZERO carries no reason, so the picker still appears there.
                */}
                {existingLotReasonNeeded && (
                  <Box sx={{ mt: 1.5 }} data-testid='existing-lot-reason'>
                    {/*
                      The question is a label, not an answer. As a floating InputLabel it sat
                      unshrunk inside an empty outlined Select — the very spot the chosen reason
                      appears in — so the field read as though it had already been answered. Stated
                      above the control instead, with the Select carrying its own placeholder until
                      a reason is picked.
                    */}
                    <FormControl
                      size='small'
                      sx={{ minWidth: 340 }}
                      error={existingLotReasonMissing}
                    >
                      <FormLabel
                        id={`existing-lot-reason-label-${idx}`}
                        sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}
                      >
                        Why is this lot already in inventory? *
                      </FormLabel>
                      <Select
                        labelId={`existing-lot-reason-label-${idx}`}
                        id={`existing-lot-reason-${idx}`}
                        displayEmpty
                        value={line.existingLotReason ?? ''}
                        renderValue={(code) =>
                          EXISTING_LOT_REASON_OPTIONS.find((o) => o.code === code)?.label ?? (
                            <Box component='span' sx={{ color: 'text.disabled' }}>
                              Select a reason
                            </Box>
                          )
                        }
                        onChange={(e) => handleChange(idx, 'existingLotReason', e.target.value)}
                        inputProps={{ 'data-testid': 'existing-lot-reason-select' }}
                      >
                        {EXISTING_LOT_REASON_OPTIONS.map(({ code, label }) => (
                          <MenuItem key={code} value={code}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                      {existingLotReasonMissing && <FormHelperText>Required</FormHelperText>}
                    </FormControl>
                  </Box>
                )}

                {/*
                  POM-6111 — the receipt is the evidence the claimed stock was real, so this is
                  where the claim gets closed. Same three answers New Stock Receipt asks for: how
                  much of this receipt was already dispensed, why the claim was open, and anything
                  the reason code cannot say. A zero tally is a complete answer - "this receipt
                  does not explain the claim" - and needs no reason.
                */}
                {lineTally && (
                  <Box sx={{ mt: 1.5 }} data-testid='lot-tally-section'>
                    <Divider sx={{ mb: 1.25 }} />
                    <Typography
                      variant='subtitle2'
                      sx={{ fontWeight: 700, color: 'primary.main', mb: 1.25 }}
                    >
                      {formatUnverifiedQty(lineTally.unverified.unverifiedQty)} units of{' '}
                      {String(line.lotNumber).trim()} {SYSTEM_DEFICIT.toLowerCase()} at{' '}
                      {existingLotFacility || 'this facility'}
                    </Typography>
                    <Grid container columnSpacing={2} rowSpacing={1.5} alignItems='flex-start'>
                      <Grid item xs={4}>
                        <MuiTextField
                          label={`Already dispensed (max ${lineTally.state.max}) *`}
                          size='small'
                          fullWidth
                          type='number'
                          inputProps={{
                            min: 0,
                            max: lineTally.state.max,
                            step: 1,
                            'data-testid': 'lot-tally-qty',
                          }}
                          value={line.tallyQty ?? lineTally.state.suggested}
                          onChange={(e) => handleChange(idx, 'tallyQty', e.target.value)}
                          error={Boolean(lineTally.state.error)}
                          helperText={lineTally.state.error || ' '}
                        />
                      </Grid>
                      <Grid item xs={8}>
                        <FormControl size='small' fullWidth error={tallyReasonMissing}>
                          {/* Explicitly tied together: without labelId/id a MUI Select renders
                              an unlabelled combobox, announced by a screen reader as just
                              "button". */}
                          <InputLabel id={`tally-reason-label-${idx}`}>Reason *</InputLabel>
                          <Select
                            labelId={`tally-reason-label-${idx}`}
                            id={`tally-reason-${idx}`}
                            label='Reason *'
                            value={line.tallyReason ?? ''}
                            onChange={(e) => handleChange(idx, 'tallyReason', e.target.value)}
                            inputProps={{ 'data-testid': 'lot-tally-reason' }}
                          >
                            {TALLY_REASON_OPTIONS.map(({ code, label }) => (
                              <MenuItem key={code} value={code}>
                                {label}
                              </MenuItem>
                            ))}
                          </Select>
                          {tallyReasonMissing && <FormHelperText>Required</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        {/* Always optional: the reason CODE above is what the audit report groups
                            on, and this carries whatever the code cannot say. */}
                        <MuiTextField
                          label='Notes'
                          size='small'
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={3}
                          placeholder='Optional'
                          value={line.tallyNotes ?? ''}
                          onChange={(e) => handleChange(idx, 'tallyNotes', e.target.value)}
                          inputProps={{ maxLength: 250, 'data-testid': 'lot-tally-notes' }}
                        />
                      </Grid>
                    </Grid>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: 'block', mt: 0.75, pl: 0.25 }}
                    >
                      {lineTally.state.received === null
                        ? 'Enter Received to see how much goes to the shelf.'
                        : [
                            ['Receiving', lineTally.state.received],
                            ['already dispensed', lineTally.state.effective],
                            ['to shelf', lineTally.state.toShelf],
                            [
                              `left as ${SYSTEM_DEFICIT.toLowerCase()}`,
                              lineTally.state.leftUnverified,
                            ],
                          ]
                            .map(([label, value]) => `${label} ${formatPreviewQty(value)}`)
                            .join(' · ')}
                    </Typography>
                  </Box>
                )}

                {/* Splits */}
                <Box sx={{ mt: 1.5, pl: 0.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography
                        variant='caption'
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        Distribution{line.splits.length > 0 ? ` (${line.splits.length})` : ''}
                      </Typography>
                      {line.splits.length > 0 && receivedQty > 0 && (
                        <Typography
                          variant='caption'
                          sx={{ color: splitOverflow ? 'error.main' : 'text.secondary' }}
                        >
                          Remaining for receiving facility: {remaining}
                          {splitOverflow && ' — over-allocated'}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size='small'
                      startIcon={<AddIcon />}
                      onClick={() => handleAddSplit(idx)}
                    >
                      Add Distribution
                    </Button>
                  </Box>

                  {line.splits.map((split, splitIdx) => {
                    const available = splitPharmacies.filter(
                      (p) =>
                        !usedPharmacyIds.includes(p.fulfillingPharmacyId) ||
                        p.fulfillingPharmacyId === split.fulfillingPharmacyId
                    );
                    return (
                      <Grid
                        key={splitIdx}
                        container
                        spacing={1}
                        alignItems='flex-start'
                        sx={{ mb: 0.5 }}
                      >
                        <Grid item xs={7}>
                          <FormControl
                            size='small'
                            fullWidth
                            error={showErrors && !split.fulfillingPharmacyId}
                          >
                            <InputLabel>Facility *</InputLabel>
                            <Select
                              label='Facility *'
                              value={split.fulfillingPharmacyId}
                              onChange={(e) =>
                                handleSplitChange(
                                  idx,
                                  splitIdx,
                                  'fulfillingPharmacyId',
                                  e.target.value
                                )
                              }
                            >
                              {available.map(({ fulfillingPharmacyId, fulfillingPharmacyName }) => (
                                <MenuItem key={fulfillingPharmacyId} value={fulfillingPharmacyId}>
                                  {fulfillingPharmacyName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                          <MuiTextField
                            label='Qty Expected *'
                            size='small'
                            fullWidth
                            type='number'
                            inputProps={{ min: 1 }}
                            value={split.quantityExpected}
                            onChange={(e) =>
                              handleSplitChange(idx, splitIdx, 'quantityExpected', e.target.value)
                            }
                            error={showErrors && Number(split.quantityExpected) < 1}
                            helperText={
                              showErrors && Number(split.quantityExpected) < 1 ? 'Min 1' : ''
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          xs={1}
                          sx={{ display: 'flex', alignItems: 'center', pt: '6px !important' }}
                        >
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => handleRemoveSplit(idx, splitIdx)}
                            aria-label='Remove distribution'
                          >
                            <DeleteOutlineIcon fontSize='small' />
                          </IconButton>
                        </Grid>
                      </Grid>
                    );
                  })}

                  {splitOverflow && (
                    <Typography variant='caption' color='error'>
                      Total distribution qty ({splitTotal}) exceeds received qty ({receivedQty}).
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}

          <Button size='small' startIcon={<AddIcon />} onClick={handleAddRow} sx={{ mt: 0.5 }}>
            Add Another Item
          </Button>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          {saveBlocked && (
            <Typography variant='caption' color='text.secondary' sx={{ mr: 'auto' }}>
              {saveBlocked}
            </Typography>
          )}
          <Button color='secondary' onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            loading={submitting}
            variant='contained'
            onClick={handleSave}
            disabled={Boolean(saveBlocked)}
          >
            Save Items
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <AddCatalogDialog open={catalogDialogOpen} onClose={() => setCatalogDialogOpen(false)} />
      <AddInventoryItemDialog open={ndcDialogOpen} onClose={() => setNdcDialogOpen(false)} />
    </>
  );
};

AddLineDialog.propTypes = {
  receivingId: PropTypes.string,
  receivedAtPharmacyId: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

AddLineDialog.defaultProps = {
  receivingId: null,
  receivedAtPharmacyId: null,
};

export default AddLineDialog;
